import { useEffect, useState } from "react";
import { Gift, Plus, Trash2, Copy, Check, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import usePageTitle from "@/hooks/use-page-title";
import { fmtDate, fmtDateTime } from "@/lib/date";

const empty = {
  initial_amount: 500,
  recipient_name: "",
  recipient_phone: "",
  recipient_email: "",
  message: "",
  expires_at: "",
  active: true,
};

export default function GiftCards() {
  usePageTitle("Gift Cards");
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { setRows((await api.get("/giftcards")).data); }
    catch { toast.error("Could not load gift cards"); }
  };

  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.initial_amount || payload.initial_amount <= 0) {
        toast.error("Amount must be > 0"); return;
      }
      await api.post("/giftcards", payload);
      toast.success("Gift card created");
      setForm(empty);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create");
    } finally { setSaving(false); }
  };

  const toggle = async (g) => {
    try { await api.patch(`/giftcards/${g.id}`, { active: !g.active }); load(); }
    catch { toast.error("Failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this gift card?")) return;
    try { await api.delete(`/giftcards/${id}`); load(); toast.success("Deleted"); }
    catch { toast.error("Failed"); }
  };

  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copied"); };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <Gift className="text-[#FF6B00]" />
        <h1 className="text-2xl font-black tracking-tight">Gift Cards</h1>
      </div>

      <form onSubmit={create} className="p-6 border-2 border-zinc-200 rounded-2xl bg-white space-y-4" data-testid="giftcard-form">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label="Amount (₹)*">
            <input type="number" min="1" value={form.initial_amount} onChange={(e) => setForm({ ...form, initial_amount: parseFloat(e.target.value) || 0 })} className="input" data-testid="gc-amount" />
          </Field>
          <Field label="Recipient Name">
            <input value={form.recipient_name} onChange={(e) => setForm({ ...form, recipient_name: e.target.value })} className="input" data-testid="gc-name" />
          </Field>
          <Field label="Recipient Phone">
            <input value={form.recipient_phone} onChange={(e) => setForm({ ...form, recipient_phone: e.target.value })} className="input" />
          </Field>
          <Field label="Recipient Email">
            <input value={form.recipient_email} onChange={(e) => setForm({ ...form, recipient_email: e.target.value })} className="input" />
          </Field>
          <Field label="Expires On">
            <input type="date" value={form.expires_at ? form.expires_at.slice(0, 10) : ""} onChange={(e) => setForm({ ...form, expires_at: e.target.value ? new Date(e.target.value).toISOString() : "" })} className="input" />
          </Field>
        </div>
        <Field label="Message">
          <input value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="input" placeholder="Happy Birthday!" />
        </Field>
        <button type="submit" disabled={saving} className="btn-swiss btn-accent" data-testid="gc-submit">
          <Plus size={14} className="mr-1" /> Generate Gift Card
        </button>
      </form>

      <div className="border-2 border-zinc-200 rounded-2xl bg-white overflow-hidden">
        <div className="p-4 font-bold border-b border-zinc-100">Gift Cards ({rows.length})</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-xs uppercase tracking-wider text-zinc-500">
              <tr>
                <th className="text-left px-4 py-3">Code</th>
                <th className="text-left px-4 py-3">Recipient</th>
                <th className="text-left px-4 py-3">Amount</th>
                <th className="text-left px-4 py-3">Balance</th>
                <th className="text-left px-4 py-3">Expires</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody data-testid="gc-rows">
              {rows.map((g) => (
                <tr key={g.id} className="border-t border-zinc-100 hover:bg-zinc-50">
                  <td className="px-4 py-3 font-mono font-bold flex items-center gap-2">
                    {g.code} <button onClick={() => copy(g.code)}><Copy size={12} className="text-zinc-400 hover:text-[#FF6B00]" /></button>
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-semibold">{g.recipient_name || "—"}</div>
                    <div className="text-xs text-zinc-500">{g.recipient_phone || g.recipient_email || ""}</div>
                  </td>
                  <td className="px-4 py-3 font-mono">₹{g.initial_amount}</td>
                  <td className="px-4 py-3 font-mono font-bold text-emerald-700">₹{g.balance}</td>
                  <td className="px-4 py-3 text-xs">{g.expires_at ? fmtDate(g.expires_at) : "—"}</td>
                  <td className="px-4 py-3">
                    <button onClick={() => toggle(g)} className={`px-2 py-0.5 text-[10px] font-bold rounded-full ${g.active ? "bg-emerald-100 text-emerald-700" : "bg-zinc-200 text-zinc-500"}`} data-testid={`gc-toggle-${g.id}`}>
                      {g.active ? <><Check size={10} className="inline" /> Active</> : <><X size={10} className="inline" /> Inactive</>}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => del(g.id)} className="text-red-500 hover:text-red-700" data-testid={`gc-del-${g.id}`}><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={7} className="text-center py-10 text-zinc-500">No gift cards yet — create one above.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider font-bold text-zinc-500 mb-1">{label}</label>
      {children}
    </div>
  );
}
